<template>
    <div class="container">
      <h2>{{ $t('titre.UserManagement') }}</h2>
  
      <!-- 🟢 Button to Open the Form Modal - Moved to Right -->
      <div class="d-flex justify-content-end mb-3">
        <button @click="openModal()" class="btn add-button">{{ $t('add_user') }}</button>
      </div>
  
      <!-- 🟢 User Table with Filters -->
      <div class="table-container">
        <table class="custom-table">
          <thead>
            <!-- 🔍 Filter Row -->
            <tr>
              <th><input v-model="filters.fullName" class="filter-input" placeholder="Filter Full Name" /></th>
              <th><input v-model="filters.username" class="filter-input" placeholder="Filter Username" /></th>
              <th><input v-model="filters.email" class="filter-input" placeholder="Filter Email" /></th>
              <th><input v-model="filters.phone" class="filter-input" placeholder="Filter Phone" /></th>
              <th>
                <select v-model="filters.status" class="filter-select">
                  <option value="">All</option>
                  <option value="enabled">Enabled</option>
                  <option value="disabled">Disabled</option>
                </select>
              </th>
              <th>
                <select v-model="filters.category" class="filter-select">
                  <option value="">All</option>
                  <option value="S">Super Admin</option>
                  <option value="A">Admin</option>
                  <option value="U">User</option>
                </select>
              </th>
              <th>Actions</th>
            </tr>
  
            <!-- 🔹 Table Header -->
            <tr>
              <th>Full Name</th>
              <th>Username</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Category</th>
              <th>Actions</th>
            </tr>
          </thead>
  
          <tbody>
            <tr v-for="user in filteredUsers" :key="user.id">
              <td>{{ user.firstName }} {{ user.lastName }}</td>
              <td>{{ user.username }}</td>
              <td>{{ user.email || "N/A" }}</td>
              <td>{{ user.phoneNumber || "N/A" }}</td>
              <td>
                <label v-if="user.userCategory !== 'S'" class="switch">
                  <input type="checkbox" :checked="user.enabled" @change="toggleUserStatus(user)">
                  <span class="slider"></span>
                </label>
              </td>
              <td>{{ getLabelOfCategoryUser(user.userCategory) }}</td>
              <td>
                <button class="btn btn-outline-primary btn-sm me-2" @click="openModal(user)">
                  <i class="fas fa-edit"></i>
                </button>
                <button class="btn btn-outline-danger btn-sm" @click="deleteUser(user.id)">
                  <i class="fas fa-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
  
      <!-- 🟢 BootstrapVue Modal for Add/Edit User -->
      <b-modal v-model="showModal" title="User Form" hide-footer>
        <form @submit.prevent="saveUser">
          <!-- 🟢 Name Fields -->
          <div class="form-group">
            <div class="input-group">
              <span class="input-group-text">👤</span>
              <input v-model="newUser.firstName" class="form-control" placeholder="First Name" required />
            </div>
            <div class="input-group">
              <span class="input-group-text">👤</span>
              <input v-model="newUser.lastName" class="form-control" placeholder="Last Name" required />
            </div>
          </div>
  
          <!-- 🟢 Username & Password -->
          <div class="form-group">
            <div class="input-group">
              <span class="input-group-text">@</span>
              <input v-model="newUser.username" class="form-control" placeholder="Username" required />
            </div>
            <div class="input-group">
              <span class="input-group-text">🔒</span>
              <input v-model="newUser.password" type="password" class="form-control" placeholder="Password" :disabled="editingUser" required />
            </div>
          </div>
  
          <!-- 🟢 Contact Info -->
          <div class="form-group">
            <div class="input-group">
              <span class="input-group-text">📧</span>
              <input v-model="newUser.email" type="email" class="form-control" placeholder="Email" required />
            </div>
            <div class="input-group">
              <span class="input-group-text">📞</span>
              <input v-model="newUser.phoneNumber" class="form-control" placeholder="Phone Number" required />
            </div>
          </div>
  
          <!-- 🟢 Form Buttons -->
          <div class="modal-buttons">
            <button type="submit" class="btn save-button">
              {{ editingUser ? "Update User" : "Add User" }}
            </button>
            <button type="button" class="btn cancel-button" @click="closeModal">Cancel</button>
          </div>
        </form>
      </b-modal>
    </div>
  </template>
  
  <script>
  import { UserApi, AuthApi } from '../api';
  
  export default {
    data() {
      return {
        users: [],
        filters: {
          fullName: "",
          username: "",
          email: "",
          phone: "",
          status: "",
          category: "",
        },
        newUser: { firstName: "", lastName: "", username: "", password: "", email: "", phoneNumber: "", enabled: true },
        editingUser: null,
        showModal: false,
      };
    },
    computed: {
      filteredUsers() {
        return this.users.filter(user => {
          const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
          return (
            (this.filters.fullName === "" || fullName.includes(this.filters.fullName.toLowerCase())) &&
            (this.filters.username === "" || user.username.toLowerCase().includes(this.filters.username.toLowerCase())) &&
            (this.filters.email === "" || (user.email && user.email.toLowerCase().includes(this.filters.email.toLowerCase()))) &&
            (this.filters.phone === "" || (user.phoneNumber && user.phoneNumber.includes(this.filters.phone))) &&
            (this.filters.status === "" || (this.filters.status === "enabled" && user.enabled) || (this.filters.status === "disabled" && !user.enabled)) &&
            (this.filters.category === "" || user.userCategory === this.filters.category)
          );
        });
      }
    },
    async created() {
      this.loadUsers();
    },
    methods: {
      async loadUsers() {
        try {
          this.users = await UserApi.listUsers();
        } catch (error) {
          console.error("Error loading users:", error);
        }
      },
      async saveUser() {
        try {
          if (this.editingUser) {
            await UserApi.updateUser(this.editingUser.id, this.newUser);
          } else {
            await AuthApi.register(this.newUser); // ✅ RESTORED THIS LINE
          }
          this.closeModal();
          this.loadUsers();
        } catch (error) {
          console.error("Error saving user:", error);
        }
      },
      async toggleUserStatus(user) {
        try {
          user.enabled = !user.enabled;
          await UserApi.updateUserStatus(user.id, { enabled: user.enabled });
        } catch (error) {
          console.error("Error updating user status:", error);
        }
      },
      async deleteUser(id) {
        if (confirm("Are you sure you want to delete this user?")) {
          try {
            await UserApi.deleteUser(id);
            this.loadUsers();
          } catch (error) {
            console.error("Error deleting user:", error);
          }
        }
      },
      openModal(user = null) {
        this.newUser = user ? { ...user } : { firstName: "", lastName: "", username: "", password: "", email: "", phoneNumber: "", enabled: true };
        this.editingUser = user;
        this.showModal = true;
      },
      getLabelOfCategoryUser(category) {
        if (category === 'S') return 'Super Admin';
        if (category === 'A') return 'Admin';
        if (category === 'U') return 'User';
        return '-';
      }
    },
  };
  </script>
  
  <style scoped>
  /* Container */
  .container {
    margin-top: 20px;
    border: 1px solid #ddd;
    padding: 10px;
    border-radius: 10px;
  }
  
  h2 {
    background-color: #47c399;
    border-radius: 10px 10px 0 0;
    color: azure;
    padding: 5px;
    text-align: justify;
    padding-left: 15px;
  }
  
  /* Table */
  .custom-table {
    width: 100%;
    border-collapse: collapse;
  }
  
  .custom-table th,
  .custom-table td {
    border: 1px solid #ddd;
    padding: 8px;
    text-align: center;
  }
  
  .custom-table thead tr {
    background-color: #f2f2f2;
    color: rgb(19, 1, 1);
  }
  
  .custom-table tbody tr:nth-child(even) {
    background-color: #fafafa;
  }
  
  .custom-table tbody tr:hover {
    background-color: #f1f1f1;
  }
  
  /* Toggle Switch */
  .switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 20px;
  }
  
  .switch input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  
  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: #ccc;
    transition: 0.4s;
    border-radius: 10px;
  }
  
  .slider:before {
    position: absolute;
    content: "";
    height: 14px;
    width: 14px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: 0.4s;
    border-radius: 50%;
  }
  
  input:checked + .slider {
    background-color: #28a745;
  }
  
  input:checked + .slider:before {
    transform: translateX(18px);
  }
  
  /* Buttons */
  .add-button {
    background-color: #007bff;
    color: white;
  }
  
  </style>
  